/**
 * Live analytics snapshots, tracked per (domain, window) — keyed
 * "{domain}:{window}" — so each watched pair is rejoined after a reconnect
 * and a 7-day overview viewer never receives a 30-day tools snapshot (the
 * server fans out to per-domain×window rooms).
 */

import type {
  AnalyticsDomain,
  AnalyticsSnapshotEvent,
  AnalyticsSnapshotEventOf,
  AnalyticsSnapshotOf,
} from '@/types/analytics/bundles'
import { WebSocketClient } from '../client'
import { SubscriptionRegistry } from '../subscriptions'

/** Map an analytics (domain, window) pair to its registry / room key. */
export function analyticsKey(domain: AnalyticsDomain, window: number): string {
  return `${domain}:${window}`
}

/**
 * The registry routes by key, which TypeScript cannot relate to the payload,
 * so each subscriber narrows the event it is handed to its own domain's arm.
 */
function isSnapshotOf<D extends AnalyticsDomain>(
  snapshot: AnalyticsSnapshotEvent,
  domain: D
): snapshot is AnalyticsSnapshotEvent & AnalyticsSnapshotEventOf<D> {
  return snapshot.domain === domain
}

/** Inverse of analyticsKey — splits on the last ':' since domain names carry none. */
export function parseAnalyticsKey(key: string): { domain: string; window: number } {
  const sep = key.lastIndexOf(':')
  return { domain: key.slice(0, sep), window: Number(key.slice(sep + 1)) }
}

export class AnalyticsLiveApi {
  private readonly snapshots: SubscriptionRegistry<string, AnalyticsSnapshotEvent>

  constructor(private readonly client: WebSocketClient) {
    this.snapshots = new SubscriptionRegistry({
      join: (key) => {
        const { domain, window } = parseAnalyticsKey(key)
        this.client.emitWhenConnected('join_analytics', { domain, window })
      },
      leave: (key) => {
        const { domain, window } = parseAnalyticsKey(key)
        this.client.emitIfSocket('leave_analytics', { domain, window })
      },
    })
  }

  /**
   * Subscribe to snapshots for one (domain, window). Joins the
   * per-pair room when its subscriber set goes empty → non-empty and leaves
   * it when the last subscriber for that pair unsubscribes.
   *
   * Args:
   *   domain: Analytics domain, one arm of AnalyticsSnapshotEvent
   *   window: Window length (days for most domains, months for trends)
   *   callback: Called with each snapshot for that (domain, window)
   */
  subscribeToAnalytics<D extends AnalyticsDomain>(
    domain: D,
    window: number,
    callback: (data: AnalyticsSnapshotOf<D>) => void
  ): () => void {
    if (!this.client.hasSocket()) {
      console.warn('WebSocket not connected. Call connect() first.')
      return () => {}
    }

    this.client.attachOnce('analytics_snapshot', (event) => {
      const snapshot = event as AnalyticsSnapshotEvent
      this.snapshots.dispatch(analyticsKey(snapshot.domain, snapshot.window), snapshot)
    })

    return this.snapshots.subscribe(analyticsKey(domain, window), (snapshot) => {
      if (isSnapshotOf(snapshot, domain)) callback(snapshot.data)
    })
  }

  /** Rejoin every watched (domain, window) room after a reconnect. */
  replay(): void {
    this.snapshots.replay()
  }

  clear(): void {
    this.snapshots.clear()
  }
}
